import { useState } from "react";

import {
  AccountFlowError,
  confirmPasswordReset,
  confirmSignUpAndSignIn,
  requestPasswordReset,
  resendConfirmationCode,
  signIn,
  SignInError,
  startSignUp,
  type ConfirmationAttempt,
  type Session,
} from "./auth/session.js";

type View = "signin" | "signup" | "confirm" | "forgot" | "reset";

const title: Readonly<Record<View, string>> = {
  signin: "Sign in",
  signup: "Create an account",
  confirm: "Confirm your email",
  forgot: "Reset your password",
  reset: "Enter your reset code",
};

/** The complete public account entry path (`ACC-001`, `ACC-006`, `ACC-007`). */
export const SignIn = ({
  onSignedIn,
}: {
  onSignedIn: (session: Session) => void;
}): React.JSX.Element => {
  const [view, setView] = useState<View>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [attempt, setAttempt] = useState<ConfirmationAttempt | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const run = async (action: () => Promise<void>): Promise<void> => {
    setError(undefined);
    setNotice(undefined);
    setBusy(true);
    try {
      await action();
    } catch (caught) {
      setError(
        caught instanceof AccountFlowError || caught instanceof SignInError
          ? caught.message
          : "The account service could not complete that request.",
      );
    } finally {
      setBusy(false);
    }
  };

  const show = (next: View): void => {
    setError(undefined);
    setNotice(undefined);
    setCode("");
    setView(next);
  };

  const requestReset = async (address = email.trim()): Promise<void> => {
    await requestPasswordReset(address);
    setView("reset");
    setCode("");
    setNotice(
      "If an account can be recovered for that address, a reset code has been sent.",
    );
  };

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();

    await run(async () => {
      switch (view) {
        case "signin": {
          try {
            onSignedIn(await signIn(email.trim(), password));
          } catch (caught) {
            if (caught instanceof SignInError && caught.needsConfirmation) {
              setAttempt({ email: email.trim(), password });
              setView("confirm");
              setNotice("Enter the code from your confirmation email.");
              return;
            }
            throw caught;
          }
          return;
        }
        case "signup": {
          const started = await startSignUp(email, password);
          setAttempt(started);
          setView("confirm");
          setCode("");
          setNotice(
            "If this address can be confirmed, a code has been sent. Check spam if it does not arrive.",
          );
          return;
        }
        case "confirm": {
          if (!attempt) {
            throw new AccountFlowError("Start signup before entering a code.");
          }
          try {
            onSignedIn(await confirmSignUpAndSignIn(attempt, code));
          } catch (caught) {
            if (
              caught instanceof AccountFlowError &&
              caught.needsPasswordReset
            ) {
              setEmail(attempt.email);
              await requestReset(attempt.email);
              return;
            }
            throw caught;
          }
          return;
        }
        case "forgot":
          await requestReset();
          return;
        case "reset":
          await confirmPasswordReset(email.trim(), code, password);
          setPassword("");
          setCode("");
          setView("signin");
          setNotice("Password updated. Sign in with the new password.");
      }
    });
  };

  const resend = async (): Promise<void> => {
    await run(async () => {
      await resendConfirmationCode(attempt?.email ?? email.trim());
      setNotice(
        "If this address can be confirmed, a new code has been sent. Check spam before requesting another.",
      );
    });
  };

  const passwordLabel = view === "reset" ? "New password" : "Password";

  return (
    <main className="shell">
      <header>
        <h1>AI Build Platform</h1>
      </header>

      <form className="panel signin" onSubmit={(event) => void submit(event)}>
        <h2>{title[view]}</h2>

        {view === "confirm" && (
          <p>
            We sent a six-digit code to <strong>{attempt?.email ?? email}</strong>.
          </p>
        )}

        {view !== "confirm" && view !== "reset" && (
          <>
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </>
        )}

        {(view === "signin" || view === "signup" || view === "reset") && (
          <>
            <label htmlFor="password">{passwordLabel}</label>
            <input
              id="password"
              type="password"
              autoComplete={view === "signin" ? "current-password" : "new-password"}
              minLength={view === "signin" ? undefined : 12}
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </>
        )}

        {(view === "confirm" || view === "reset") && (
          <>
            <label htmlFor="code">
              {view === "confirm" ? "Confirmation code" : "Reset code"}
            </label>
            <input
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </>
        )}

        <button type="submit" disabled={busy}>
          {busy
            ? "Working…"
            : view === "signin"
              ? "Sign in"
              : view === "signup"
                ? "Send confirmation code"
                : view === "confirm"
                  ? "Confirm and continue"
                  : view === "forgot"
                    ? "Send reset code"
                    : "Set new password"}
        </button>

        {error !== undefined && (
          <p className="blocked" role="alert">
            {error}
          </p>
        )}
        {notice !== undefined && (
          <p className="notice" role="status">
            {notice}
          </p>
        )}

        <nav className="account-actions" aria-label="Account actions">
          {view === "signin" && (
            <>
              <button type="button" onClick={() => show("signup")}>
                Create an account
              </button>
              <button type="button" onClick={() => show("forgot")}>
                Forgot password?
              </button>
            </>
          )}
          {view === "confirm" && (
            <button type="button" disabled={busy} onClick={() => void resend()}>
              Resend confirmation code
            </button>
          )}
          {view !== "signin" && (
            <button type="button" onClick={() => show("signin")}>
              Back to sign in
            </button>
          )}
        </nav>
      </form>
    </main>
  );
};
